import MobileLinkPrompt from '@/components/MobileLinkPrompt';
import { authDestination } from '@/components/OTPEntry';
import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import customerApi from '@/lib/marketplaceCustomerApi';

const fieldClass = 'mt-2 w-full rounded-xl border border-slate-300 px-4 py-3';
const errorMessage = (error) => typeof error?.response?.data?.detail === 'string'
  ? error.response.data.detail : 'Please check your details and try again.';

export default function MarketplaceAccount() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const requestedReturn = params.get('returnTo') || '';
  const returnTo = /^\/marketplace\/product\/[a-zA-Z0-9_-]+$/.test(requestedReturn) ? requestedReturn : '';
  const [customer, setCustomer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [challenge, setChallenge] = useState('');
  const [proof, setProof] = useState(null);
  const [channel, setChannel] = useState('');
  const [form, setForm] = useState({ phone: '', email: '', name: '' });
  const [code, setCode] = useState('');
  const [orders, setOrders] = useState([]);
  const [address, setAddress] = useState('');

  useEffect(() => {
    let mounted = true;
    customerApi.get('/me').then(({ data }) => {
      if (mounted) { setCustomer(data); setForm({ phone: data.phone, email: data.email, name: data.name || '' }); }
    }).catch((err) => {
      if (mounted && err.response?.status !== 401) setError('Customer account is temporarily unavailable. Please try again.');
    }).finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, []);

  useEffect(() => {
    if (!customer) return;
    customerApi.get('/orders').then(({ data }) => setOrders(data.orders || []))
      .catch(() => setError('Your order history could not be loaded. Please try again.'));
  }, [customer]);

  const submit = async (event) => {
    event.preventDefault(); setBusy(true); setError(''); setMessage('');
    try {
      if (!challenge) {
        const { data } = await customerApi.post('/otp/request', form);
        setChallenge(data.challenge_id); setChannel(data.channel || 'email'); setMessage(data.message);
      } else {
        const { data } = await customerApi.post('/otp/verify', { challenge_id: challenge, code });
        if (data.status === 'registration_required') { setProof(data); return; }
        if (data.status === 'authenticated') {
          if (data.account_type !== 'customer') { window.location.assign(authDestination[data.account_type]); return; }
          const current = await customerApi.get('/me'); setCustomer(current.data);
        } else { setCustomer(data); }
        if (returnTo) navigate(`${returnTo}?checkout=1`, { replace: true });
      }
    } catch (err) { setError(errorMessage(err)); }
    finally { setBusy(false); }
  };

  const saveAddress = async (event) => {
    event.preventDefault(); setBusy(true); setError('');
    try {
      await customerApi.post('/addresses', { name: form.name, address });
      const { data } = await customerApi.get('/me');
      setCustomer(data); setAddress(''); setMessage('Delivery address saved.');
    } catch (err) { setError(errorMessage(err)); }
    finally { setBusy(false); }
  };

  return <main className="min-h-screen bg-slate-50 px-4 pb-16 pt-28">
    <div className="mx-auto max-w-3xl">
      <Link to="/marketplace" className="font-bold text-purple-800">← Browse marketplace</Link>
      <section className="mt-6 rounded-3xl bg-white p-6 shadow-sm sm:p-10">
        <p className="text-sm font-bold uppercase tracking-widest text-purple-800">KalaManch Marketplace</p>
        <h1 className="mt-3 text-3xl font-black">{customer ? 'Your customer account' : 'Marketplace customer sign in'}</h1>
        <p className="mt-3 text-slate-600">For shopping, delivery addresses and orders. Artist and collaborator accounts remain separate.</p>
        {error && <p role="alert" className="mt-4 rounded-xl bg-red-50 p-4 text-red-800">{error}</p>}
        {message && <p role="status" className="mt-4 rounded-xl bg-green-50 p-4 text-green-800">{message}</p>}
        {loading ? <p className="mt-6">Checking customer login…</p> : !customer && proof ? <div className="space-y-3 mt-6"><h2 className="text-xl font-bold">Choose your account type</h2>{['artist', 'collaborator', 'customer'].map(role => <button className="btn-outline block w-full capitalize" key={role} onClick={() => navigate('/sign-in', { state: { proof, selectedRole: role, returnTo } })}>{role}</button>)}</div> : !customer ? <form onSubmit={submit} className="mt-6 space-y-4">
          <p className="rounded-xl bg-orange-50 p-4 text-sm">Sign in with a WhatsApp code. If WhatsApp is unavailable, your code will be sent by email. New users choose an account type after verification.</p>
          {!challenge ? <>
            <label className="block">Mobile number<input className={fieldClass} type="tel" autoComplete="tel" placeholder="+91" required maxLength={32} value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} /></label>
            <label className="block">Email for verification<input className={fieldClass} type="email" autoComplete="email" required maxLength={254} value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} /></label>
            <label className="block">Your name<input className={fieldClass} autoComplete="name" required maxLength={160} value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} /></label>
            <p className="text-sm text-slate-500">Returning customers: use the mobile number and email you registered with.</p>
          </> : <>
            <label className="block">Six-digit {channel} code<input className={fieldClass} inputMode="numeric" autoComplete="one-time-code" pattern="[0-9]{6}" maxLength={6} required value={code} onChange={e => setCode(e.target.value)} /></label>
            <button type="button" className="text-purple-800 underline" disabled={busy} onClick={() => { setChallenge(''); setCode(''); setMessage(''); }}>Change details or request a new code</button>
          </>}
          <button className="btn-primary block w-full" disabled={busy}>{busy ? 'Please wait…' : challenge ? 'Verify and continue' : 'Send code'}</button>
        </form> : <div className="mt-6 space-y-6">
          <MobileLinkPrompt account={customer} accountType="customer" />
          <p className="font-semibold">{customer.name} · {customer.phone} · {customer.email}</p>
          {returnTo && <Link className="btn-primary inline-block" to={`${returnTo}?checkout=1`}>Continue to your product</Link>}
          <section><h2 className="text-xl font-bold">Delivery addresses</h2>
            {(customer.addresses || []).map(item => <p key={item.id} className="mt-3 rounded-xl bg-slate-50 p-4">{item.name}<br />{item.address}</p>)}
            <form onSubmit={saveAddress} className="mt-4 space-y-3">
              <label className="block">Recipient name<input className={fieldClass} required maxLength={160} value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} /></label>
              <label className="block">New delivery address<textarea className={fieldClass} required minLength={5} maxLength={1200} value={address} onChange={e => setAddress(e.target.value)} placeholder="House, street, city, state and PIN code" /></label>
              <button className="btn-outline" disabled={busy}>Save address</button>
            </form>
          </section>
          <section><h2 className="text-xl font-bold">Your orders</h2>
            {!orders.length && <p className="mt-3 text-slate-600">No orders yet.</p>}
            {orders.map(order => <article key={order.id} className="mt-3 rounded-xl border border-slate-200 p-4">
              <h3 className="font-bold">{order.product_title}</h3><p>Quantity {order.quantity} · ₹{order.total_inr}</p>
              <p>Status: {order.status} · Payment: {order.payment_status}</p>
              <p>Delivery: {order.delivery_status || 'pending'}{order.tracking_reference ? ` · Tracking: ${order.tracking_reference}` : ''}</p>
            </article>)}
          </section>
          <button className="btn-outline" disabled={busy} onClick={async () => {
            setBusy(true); setError('');
            try { await customerApi.post('/logout'); setCustomer(null); setOrders([]); setChallenge(''); setCode(''); setMessage('Signed out of marketplace.'); }
            catch (err) { setError(errorMessage(err)); } finally { setBusy(false); }
          }}>Sign out of marketplace</button>
        </div>}
      </section>
    </div>
  </main>;
}
